/**
 * Formulario de proveedor
 * 
 * Permite crear y editar proveedores del sistema.
 * 
 * @module pages/proveedores/ProveedorForm
 * @requires react, react-router-dom, ../../services/proveedores.service
 * @related_files ./Proveedores.js
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

// Servicios
import proveedoresService from '../../services/proveedores.service';

// Componentes
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';

// Iconos
import { 
  FaSave, FaTimes, FaBuilding, FaUserTie, FaPhone, 
  FaEnvelope, FaMapMarkerAlt, FaGlobe, FaIdCard,
  FaTruck
} from 'react-icons/fa';

/**
 * Componente de formulario de proveedor
 * @returns {JSX.Element} Componente ProveedorForm
 */
const ProveedorForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const esEdicion = Boolean(id);
  const returnTo = String(searchParams.get('returnTo') || '').trim();
  const [creadoId, setCreadoId] = useState(null);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    contacto: '',
    telefono: '',
    email: '',
    direccion: '',
    ciudad: '',
    sitio_web: '',
    cuit: '',
    condicion_iva: 'Responsable Inscripto',
    observaciones: '',
    activo: true
  });
  
  const [loading, setLoading] = useState(false);
  const [loadingDatos, setLoadingDatos] = useState(false);
  const [errors, setErrors] = useState({});
  
  /**
   * Carga los datos del proveedor si es edición
   */
  useEffect(() => {
    if (esEdicion) {
      cargarProveedor();
    }
  }, [id]);
  
  /**
   * Carga los datos del proveedor para edición
   */
  const cargarProveedor = async () => {
    try {
      setLoadingDatos(true);
      const proveedor = await proveedoresService.obtenerPorId(id);
      
      setFormData({
        nombre: proveedor.nombre || '',
        contacto: proveedor.contacto || '',
        telefono: proveedor.telefono || '',
        email: proveedor.email || '',
        direccion: proveedor.direccion || '',
        ciudad: proveedor.ciudad || '',
        sitio_web: proveedor.sitio_web || '',
        cuit: proveedor.cuit || '',
        condicion_iva: proveedor.condicion_iva || 'Responsable Inscripto',
        observaciones: proveedor.observaciones || '',
        activo: proveedor.activo !== false
      });
    } catch (error) {
      console.error('Error al cargar proveedor:', error);
      toast.error('Error al cargar los datos del proveedor');
      navigate('/proveedores');
    } finally {
      setLoadingDatos(false);
    }
  };
  
  /**
   * Maneja los cambios en los inputs del formulario
   * @param {Event} e - Evento del input
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpiar error del campo al modificarlo
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  /**
   * Valida los datos del formulario
   * @returns {boolean} true si es válido, false si hay errores
   */
  const validarFormulario = () => {
    const newErrors = {};
    
    // Nombre es obligatorio
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre del proveedor es requerido';
    }
    
    // Validar email si se proporciona
    if (formData.email && !isValidEmail(formData.email)) {
      newErrors.email = 'El email no es válido';
    }
    
    // Validar teléfono si se proporciona (solo números, guiones y espacios)
    if (formData.telefono && !/^[\d\s\-\+\(\)]+$/.test(formData.telefono)) {
      newErrors.telefono = 'El teléfono solo puede contener números, espacios y guiones';
    }
    
    // Validar CUIT si se proporciona
    if (formData.cuit && !/^\d{2}-?\d{8}-?\d{1}$/.test(formData.cuit.replace(/\s/g, ''))) {
      newErrors.cuit = 'El CUIT debe tener el formato XX-XXXXXXXX-X';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  /**
   * Valida formato de email
   * @param {string} email - Email a validar
   * @returns {boolean} true si es válido
   */
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  
  /**
   * Maneja el envío del formulario
   * @param {Event} e - Evento del formulario
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) {
      toast.error('Por favor, corrige los errores del formulario');
      return;
    }
    
    setLoading(true);
    
    try {
      if (esEdicion) {
        await proveedoresService.actualizar(id, formData);
        toast.success('Proveedor actualizado correctamente');
        navigate('/proveedores');
      } else {
        const resultado = await proveedoresService.crear(formData);
        const nuevoId = resultado?.id || resultado?.data?.id || null;
        toast.success('Proveedor creado correctamente');
        if (returnTo.startsWith('/compras/nueva') && nuevoId) {
          navigate(`/compras/nueva?proveedorId=${encodeURIComponent(nuevoId)}`);
        } else if (returnTo.startsWith('/compras/nueva')) {
          navigate('/compras/nueva');
        } else if (nuevoId) {
          setCreadoId(nuevoId);
        } else {
          navigate('/proveedores');
        }
      }
    } catch (error) {
      console.error('Error al guardar proveedor:', error);
      
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(esEdicion ? 'Error al actualizar el proveedor' : 'Error al crear el proveedor');
      }
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Cancela la operación y vuelve al listado
   */
  const handleCancel = () => {
    navigate('/proveedores');
  };
  
  if (loadingDatos) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (creadoId) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <Card>
          <h1 className="text-xl font-bold text-e0e3-foreground">Proveedor creado</h1>
          <p className="mt-2 text-sm text-e0e3-muted">
            Ya podés usarlo en compras o revisar su cuenta corriente.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to={`/compras/nueva?proveedorId=${encodeURIComponent(creadoId)}`}>
              <Button color="primary" icon={<FaTruck />}>
                Crear una compra con este proveedor
              </Button>
            </Link>
            {returnTo.startsWith('/compras') && (
              <Link to={returnTo}>
                <Button color="secondary">Volver a la compra</Button>
              </Link>
            )}
            <Link to="/proveedores">
              <Button color="secondary">Ver proveedores</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const fieldErrorClass = 'border-red-400 focus:border-red-400 focus:ring-red-400/25';

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-e0e3-foreground flex items-center">
          <FaTruck className="mr-3 text-e0e3-accent" />
          {esEdicion ? 'Editar Proveedor' : 'Nuevo Proveedor'}
        </h1>
        <p className="text-e0e3-muted mt-1">
          {esEdicion
            ? 'Modifica los datos del proveedor'
            : 'Registra un nuevo proveedor en el sistema'}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-e0e3-foreground mb-2">
                Nombre del Proveedor *
              </label>
              <div className="relative">
                <FaBuilding className="absolute left-3 top-1/2 -translate-y-1/2 text-e0e3-muted pointer-events-none" />
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  className={`nexo-field pl-10 pr-3 ${errors.nombre ? fieldErrorClass : ''}`}
                  placeholder="Nombre del proveedor"
                  autoComplete="organization"
                />
              </div>
              {errors.nombre && (
                <p className="mt-1 text-sm text-red-300">{errors.nombre}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-e0e3-foreground mb-2">
                Persona de Contacto
              </label>
              <div className="relative">
                <FaUserTie className="absolute left-3 top-1/2 -translate-y-1/2 text-e0e3-muted pointer-events-none" />
                <input
                  type="text"
                  name="contacto"
                  value={formData.contacto}
                  onChange={handleInputChange}
                  className="nexo-field pl-10 pr-3"
                  placeholder="Nombre del contacto"
                  autoComplete="name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-e0e3-foreground mb-2">
                Teléfono
              </label>
              <div className="relative">
                <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-e0e3-muted pointer-events-none" />
                <input
                  type="text"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  className={`nexo-field pl-10 pr-3 ${errors.telefono ? fieldErrorClass : ''}`}
                  placeholder="Teléfono de contacto"
                  autoComplete="tel"
                />
              </div>
              {errors.telefono && (
                <p className="mt-1 text-sm text-red-300">{errors.telefono}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-e0e3-foreground mb-2">
                Email
              </label>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-e0e3-muted pointer-events-none" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`nexo-field pl-10 pr-3 ${errors.email ? fieldErrorClass : ''}`}
                  placeholder="correo@ejemplo.com"
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-300">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-e0e3-foreground mb-2">
                Sitio Web
              </label>
              <div className="relative">
                <FaGlobe className="absolute left-3 top-1/2 -translate-y-1/2 text-e0e3-muted pointer-events-none" />
                <input
                  type="text"
                  name="sitio_web"
                  value={formData.sitio_web}
                  onChange={handleInputChange}
                  className="nexo-field pl-10 pr-3"
                  placeholder="www.ejemplo.com"
                  autoComplete="url"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-e0e3-foreground mb-2">
                Dirección
              </label>
              <div className="relative">
                <FaMapMarkerAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-e0e3-muted pointer-events-none" />
                <input
                  type="text"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleInputChange}
                  className="nexo-field pl-10 pr-3"
                  placeholder="Dirección completa"
                  autoComplete="street-address"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-e0e3-foreground mb-2">
                Ciudad
              </label>
              <input
                type="text"
                name="ciudad"
                value={formData.ciudad}
                onChange={handleInputChange}
                className="nexo-field"
                placeholder="Ciudad"
                autoComplete="address-level2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-e0e3-foreground mb-2">
                CUIT
              </label>
              <div className="relative">
                <FaIdCard className="absolute left-3 top-1/2 -translate-y-1/2 text-e0e3-muted pointer-events-none" />
                <input
                  type="text"
                  name="cuit"
                  value={formData.cuit}
                  onChange={handleInputChange}
                  className={`nexo-field pl-10 pr-3 ${errors.cuit ? fieldErrorClass : ''}`}
                  placeholder="XX-XXXXXXXX-X"
                />
              </div>
              {errors.cuit && (
                <p className="mt-1 text-sm text-red-300">{errors.cuit}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-e0e3-foreground mb-2">
                Condición IVA
              </label>
              <select
                name="condicion_iva"
                value={formData.condicion_iva}
                onChange={handleInputChange}
                className="nexo-field"
              >
                <option value="Responsable Inscripto">Responsable Inscripto</option>
                <option value="Monotributo">Monotributo</option>
                <option value="Exento">Exento</option>
                <option value="Consumidor Final">Consumidor Final</option>
                <option value="No Responsable">No Responsable</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-e0e3-foreground mb-2">
                Observaciones
              </label>
              <textarea
                name="observaciones"
                value={formData.observaciones}
                onChange={handleInputChange}
                rows="3"
                className="nexo-field"
                placeholder="Notas adicionales sobre el proveedor"
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="activo"
                  checked={formData.activo}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-e0e3-accent focus:ring-e0e3-accent/40 border-white/20 rounded bg-white/5"
                />
                <span className="ml-2 text-sm text-e0e3-foreground">
                  Proveedor activo
                </span>
              </label>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-white/10 flex justify-end space-x-3">
            <Button
              type="button"
              color="secondary"
              onClick={handleCancel}
              icon={<FaTimes />}
            >
              Cancelar
            </Button>

            <Button
              type="submit"
              color="primary"
              loading={loading}
              icon={<FaSave />}
            >
              {esEdicion ? 'Actualizar' : 'Guardar'} Proveedor
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
};

export default ProveedorForm;